import onResize from '../utils/onResize';

const MOBILE_NAV_MAX_DEPTH = 5;

const query = (selector, el = document) => el.querySelector(selector);
const queryAll = (selector, el = document) => [...el.querySelectorAll(selector)];

function getDefaultPanelFromUri(uriParts) {
    const effectivePageDepth = uriParts.length + 1; // we offset by one from the root for easier future math.
    const effectiveMaxDepth = MOBILE_NAV_MAX_DEPTH - 1; // we offset max depth by one because of how panels are created

    if (effectivePageDepth >= effectiveMaxDepth) {
        return effectiveMaxDepth;
    }

    // if we're at the root the uriParts will be [""]
    if (effectivePageDepth === 2 && uriParts[0] === '') {
        return 1;
    }
    return effectivePageDepth;
}

function mobileNavTarget(panelNumber, uriParts) {
    const target_parts = ['nav-panel', String(panelNumber)];

    if (uriParts) {
        // we need to calculate a slug. The slug never goes deeper than the panel depth though.
        target_parts.push(uriParts.slice(0, panelNumber - 1).join('-')); // panels are 1-indexed, so we offset
    }
    return target_parts.join('-');
}

function getActivePanelId(panelNumber, uriParts) {
    if (panelNumber === 1) {
        // top level panel has no slug components, so we have everything we need to generate the nav target.
        return mobileNavTarget(panelNumber);
    }

    const panelId = mobileNavTarget(panelNumber, uriParts);
    if (!query(`#${ panelId }`)) {
        // the target panel does not actually exist in the DOM. This will likely be because the target page
        // has no children in the mobile menu, so a panel was not generated for it. Therefore, we want its parent.
        return getActivePanelId(panelNumber - 1, uriParts);
    }

    return panelId;
}

export default class MobileNav {
    state = {
        open: false,
        activePanelClass: '',
        defaultPanel: '',
        defaultMenuId: '',
    };

    constructor(mobileNavEl) {
        this.menuEl = mobileNavEl || query('.mobile-nav');
        this.containerEl = query('html');
        this.forwardButtons = queryAll('[data-nav-target]', this.menuEl);
        this.backButtons = queryAll('[data-nav-back]', this.menuEl);
        this.navPanelItems = queryAll('[data-nav-panel-items]', this.menuEl);
        this.menuTriggers = queryAll('[data-mobile-nav-toggle]');
        this.menuTriggers.forEach(el => { el.addEventListener('click', this.toggleMenu); });
        this.findDefaultMenuState();

        onResize(() => this.closeMenu());
    }

    bindNavButtonEvents = () => {
        this.forwardButtons.forEach(el => { el.addEventListener('click', this.handleForwardClick); });
        this.backButtons.forEach(el => { el.addEventListener('click', this.handleBackClick); });
    };

    unBindNavButtonEvents = () => {
        this.forwardButtons.forEach(el => { el.removeEventListener('click', this.handleForwardClick); });
        this.backButtons.forEach(el => { el.removeEventListener('click', this.handleBackClick); });
    };

    track = (direction) => {
        if (window.GA) {
            window.GA.track(`${ direction } menu`, 'Mobile navigation', 'Click');
        }
    };

    log = (...args) => {
        if (__DEVELOPMENT) {
            console.log(...args);
        }
    };

    toggleMenu = (ev) => {
        ev.preventDefault();
        this.track(this.state.open ? 'Close' : 'Open');
        // eslint-disable-next-line no-unused-expressions
        this.state.open
            ? this.closeMenu()
            : this.openMenu();
    };

    // --
    // This function is carried over from mas public and adjusted.
    // It needs a rewrite. No time right now.
    findDefaultMenuState() {
        const uri = decodeURIComponent(window.location.pathname); // Decoding ensures this module works with macrons in uri parts.
        const uriParts = uri.replace(/^\//, '').replace(/\/\s*$/, '').split('/');

        let defaultPanelNumber = getDefaultPanelFromUri(uriParts);
        const defaultPanelId = getActivePanelId(defaultPanelNumber, uriParts);
        const checkPanelNumber = defaultPanelId.replace(/^\D+/g, '');
        if (defaultPanelNumber.toString() !== checkPanelNumber) {
            defaultPanelNumber = parseInt(checkPanelNumber, 10);
        }
        this.state.defaultPanel = `${ defaultPanelNumber }`;
        this.state.defaultMenuId = defaultPanelId;
    }

    setDefaultMenuState() {
        const { defaultPanel, defaultMenuId } = this.state;
        this.navigate(defaultPanel, defaultMenuId, true);
    }

    openMenu() {
        this.setDefaultMenuState();
        this.containerEl.classList.add('mobile-nav--visible');
        this.containerEl.classList.add('-header-reversed', '-header-over-banner');
        this.containerEl.classList.remove('mobile-nav--hidden');
        this.bindNavButtonEvents();
        this.state.open = true;
    }

    closeMenu() {
        this.containerEl.classList.remove('mobile-nav--visible');
        this.containerEl.classList.remove('-header-reversed', '-header-over-banner');
        this.containerEl.classList.add('mobile-nav--hidden');
        this.unBindNavButtonEvents();
        this.state.open = false;

        // delay removal of active panel so active nav doesn't reset while menu transitions out.
        setTimeout(() => {
            // if the menu has been opened again within in this timeout
            // don't remove the new active classes
            if (this.state.open === false) {
                if (this.state.activePanelClass) {
                    this.containerEl.classList.remove(this.state.activePanelClass);
                }
                this.state.activePanelClass = '';
            }
        }, 200);
    }

    setActivePanel = (targetPanel) => {
        const { activePanelClass } = this.state;

        if (activePanelClass) {
            this.containerEl.classList.remove(activePanelClass);
        }

        const nextActiveClass = `mobile-nav--panel-${ targetPanel }-active`;
        this.containerEl.classList.add(nextActiveClass);
        this.state.activePanelClass = nextActiveClass;
    };

    navigate = (targetPanel, targetNavId, isDefaultLoad = false) => {
        // eslint-disable-next-line no-param-reassign
        this.navPanelItems.forEach(el => el.style.display = 'none');
        let targetItemsSet = false;
        if (targetNavId) {
            const navTarget = query(`#${ targetNavId }`, this.menuEl);
            navTarget.style.display = 'block';
            targetItemsSet = true;
        }
        if (isDefaultLoad && !targetItemsSet) {
            targetPanel = 1;
        }
        this.setActivePanel(targetPanel);
    };

    handleForwardClick = (ev) => {
        ev.preventDefault();

        const targetPanel = ev.target.getAttribute('data-target-panel');
        const targetNavId = ev.target.getAttribute('data-nav-target');

        this.navigate(targetPanel, targetNavId);
    };

    handleBackClick = (ev) => {
        ev.preventDefault();
        const targetPanel = ev.target.getAttribute('data-target-panel');
        const targetNavId = ev.target.getAttribute('data-nav-back');

        this.navigate(targetPanel, targetNavId);
    }
}
